import React, { useEffect, useRef, useState } from "react"

import { ReactComponent as IconChevronDown } from "@common_assets/svg/chevron-down.svg"
import { ReactComponent as SvgPhoto } from "@common_assets/svg/photo.svg"
import { ReactComponent as IconThreeDots } from "@common_assets/svg/threeDots.svg"
import {
  Box,
  LinearProgress,
  Stack,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import { useMutationSaveGroupFilter } from "api/reactQuery/mutations/groups"
import { useQueryClassesStudents } from "api/reactQuery/queries/classesStudents"
import { useQueryExportClassStudents } from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import {
  useQueryGroupFilter,
  useQueryGroups,
} from "api/reactQuery/queries/groups"
import ListingDisplayModeButtons from "components/common/button/listingDisplayModeButtons"
import ErrorText from "components/common/error/errorText"
import { ExportPendingBanner, InlineExportSubmenu } from "components/common/exportMenu"
import GroupFilterMenu from "components/common/groupFilterMenu"
import NoElementsMessage from "components/common/listing/noElementsMessage"
import NoMatchesForSearch from "components/common/listing/noMatchesForSearch"
import LoadingIndicator from "components/common/loadingIndicator"
import { PopOverStyle, PopRow } from "components/common/poprow"
import ProcessingOverlay from "components/common/processingOverlay"
import SearchbarFormWithButtons from "components/form/searchbarFormWithButtons"
import { useArchivedClassGuard } from "hooks/archivedClassGuard"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useDialog } from "hooks/dialog"
import { useExportGuard, useExportTasks } from "hooks/exportReports"
import { useSelectedItems } from "hooks/listing"
import { useListItemActions } from "hooks/listing/listItemActions"
import { useClassId } from "hooks/navigation"
import { useCustomPayment } from "hooks/payment"
import { usePopover } from "hooks/popover"
import { useResetBalanceTask } from "hooks/resetBalanceTask"
import { userIsFreeDemoSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch, useAppSelector } from "hooks/store"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useQueryClient } from "react-query"
import { useLocation } from "react-router"
import { selectUser } from "store/auth/auth.selectors"
import { selectDisplayMode } from "store/displayMode/displayMode.selectors"
import {
  selectClassList,
  selectClassname,
  selectIsNextPage,
  selectPage,
  selectStudentsNumber,
  selectTaskData,
} from "store/utility/utility.selectors"
import {
  setClassList,
  setIsNextPage,
  setPage,
  setStudentsNumber,
} from "store/utility/utility.slice"
import { PrimaryTile } from "styles/common/tile"
import { IObjectStudentDetailed } from "ts/interfaces/Student"
import { getErrorMessageFromTab } from "utils/api"
import { schoolPlanToExportData } from "utils/export"
import { filteredData } from "utils/filterClassList"
import { isSchoolPlan } from "utils/roleCheck"

import DialogUpgradeAccount from "../../../../common/dialogUpgradeAccount/DialogUpgradeAccount"
import BatchActionButtons from "./batchActionButtons"
import DialogAddAllStudents from "./dialogAddAllStudents"
import DialogFormStudentsInfo from "./dialogFormStudentsInfo"
import DialogImport from "./dialogImport"
import ListItemActionsHandler from "./listItemActionsHandler"
import PopoverSearchbarButton from "./popoverSearchbarButton"
import ProgressBar from "./progressBar"
import { IProps, ListItemActions } from "./ViewCommonElementsContainer.types"

export type HandleFunction = {
  triggerRefetch: () => void
}

const PopoverBox = styled(Box)({
  display: "flex",
  columnGap: "8px",
  alignItems: "center",
})

const ViewCommonElementsContainer: React.ForwardRefRenderFunction<
  HandleFunction,
  IProps
> = (
  {
    sortBy,
    setIsToggle,
    sorting,
    renderDataFromBE,
    isToggleAll,
    renderDataFromBEOutside,
    stateFile,
    setSortBy,
    isEditClass,
    savingsAccountOption,
  },
  ref,
) => {
  const bottomViewRef = useRef<HTMLDivElement | null>(null)
  const previousClassname = useRef<string | null>(null)
  const userInfo = useAppSelector(selectUser)
  const { t } = useTranslation()
  const theme = useTheme()
  const classname = useAppSelector(selectClassname)
  const { showSnackbar } = useCustomSnackbar()
  const dispatch = useAppDispatch()
  const classList = useAppSelector(selectClassList)
  const page = useAppSelector(selectPage)
  const isNextPage = useAppSelector(selectIsNextPage)
  const displayMode = useAppSelector(selectDisplayMode)
  const location = useLocation()
  const classId = useClassId()
  const studentsNumber = useAppSelector(selectStudentsNumber)
  const firstRender = useRef(false)
  const isRefetchFromUseEffect = useRef(false)
  const isTablet = useIsBreakpointUp("tablet")
  const previousIsTablet = useRef<boolean | null>(null)

  const taskData = useAppSelector(selectTaskData)
  const { ref: inViewRef, inView } = useInView()
  const queryClient = useQueryClient()

  const { file, setFile } = stateFile
  const [filterPage, setFilterPage] = useState(0)
  const [filterElements, setFilterElements] = useState<
    IObjectStudentDetailed[]
  >([])
  const tableContainerRef = useRef<HTMLDivElement | null>(null)

  // Group filter (ticket 16) — gated the same way the GROUP column itself is: hidden for
  // FREE/DEMO plans and while the class is archived (the whole page is already blurred/
  // blocked by useArchivedClassGuard at that point, this just avoids an extra 404 fetch).
  const isFreeDemo = userIsFreeDemoSchoolPlan()
  const { isArchived } = useArchivedClassGuard()
  const [groupFilterAnchor, setGroupFilterAnchor] =
    useState<HTMLElement | null>(null)
  const [overflowMenuAnchor, setOverflowMenuAnchor] =
    useState<HTMLElement | null>(null)
  const [isExportRowOpen, setIsExportRowOpen] = useState(false)

  const { data: groupsData } = useQueryGroups({
    classId: Number(classId),
    options: { enabled: !isFreeDemo && !isArchived && !!classId },
  })
  const allGroups = groupsData?.data ?? []
  const hasGroups = allGroups.length > 0
  // The group-filter menu item is always visible; it's only ever unusable for two
  // distinct reasons that need distinct handling: FREE/DEMO plans don't have groups at
  // all (upsell), while a paid plan with zero groups created yet just needs one first.
  const isGroupFilterDisabled = !isFreeDemo && !hasGroups

  const { data: groupFilterData } = useQueryGroupFilter({
    classId: Number(classId),
    options: { enabled: !isFreeDemo && !isArchived && !!classId },
  })
  // A stale `students_tab` selection (e.g. saved before every group in the class was
  // deleted) must not hide the roster once there's nothing left to filter by — treat it
  // as no filter (null) rather than "filter to zero groups" whenever hasGroups is false.
  const studentsTabFilter = hasGroups
    ? groupFilterData?.data.students_tab ?? null
    : null
  const isGroupFilterActive = studentsTabFilter !== null
  const isGroupFilterHidingEverything =
    isGroupFilterActive && studentsTabFilter.length === 0

  const { mutate: saveGroupFilter } = useMutationSaveGroupFilter()

  const { popoverAnchorEl, handleClose, setPopoverAnchorEl } =
    usePopover<HTMLButtonElement>()

  const {
    isOpen: isOpenInfo,
    handleOpen: handleOpenInfo,
    handleClose: handleCloseInfo,
  } = useDialog()

  const {
    isOpen: isOpenImport,
    handleOpen: handleOpenImport,
    handleClose: handleCloseImport,
  } = useDialog()

  const {
    isOpen: isOpenAllStudents,
    handleOpen: handleOpenAllStudents,
    handleClose: handleCloseAllStudents,
  } = useDialog()

  const { action, resetActionState, invokeListItemAction } = useListItemActions<
    IObjectStudentDetailed,
    ListItemActions
  >()

  const {
    isOpen: isDialogOpenUpgradeAccount,
    handleOpen: handleOpenDialogUpgradeAccount,
    handleClose: handleCloseDialogUpgradeAccount,
  } = useDialog()

  const {
    isOpen: isOpenExport,
    handleOpen: handleOpenExport,
    handleClose: handleCloseExport,
  } = useDialog()

  const {
    isOpen: isOpenGroupsUpgrade,
    handleOpen: handleOpenGroupsUpgrade,
    handleClose: handleCloseGroupsUpgrade,
  } = useDialog()

  const { payIt, isLoadingPayments } = useCustomPayment()

  const {
    successTasks,
    pendingTasks,
    refreshTasks,
    setExportReportIdToDownload,
    handleExportSuccess,
  } = useExportTasks({
    exportType: ExportType.CLASS_STUDENTS,
    classId: Number(classId),
  })

  const {
    isProcessingBalance,
    status: resetBalanceStatus,
    handleOutcome: handleResetBalanceOutcome,
  } = useResetBalanceTask({
    classId,
    onOutcome: (outcome) => {
      if (outcome.toast) {
        showSnackbar({
          title: t(outcome.toast.titleKey),
          variant: outcome.toast.variant,
          ...(outcome.toast.variant === "error" && {
            options: { persist: true },
          }),
        })
      }

      if (outcome.invalidateStudents) {
        queryClient.invalidateQueries("classesStudents")
        dispatch(setPage(0))
        dispatch(setIsNextPage(false))
        dispatch(setClassList([]))
        setTimeout(() => refetch(), 0)
      }
    },
    onPollError: (err) => {
      showSnackbar({
        title: getErrorMessageFromTab(err),
        variant: "error",
      })
    },
  })

  // A non-empty group-filter selection behaves like the existing text search: a single,
  // server-filtered page rather than infinite-scroll accumulation (ticket 16). An empty
  // selection ("hide every group") is handled separately below — the query never fires,
  // avoiding any ambiguity over how BED treats an empty `group_ids` array server-side.
  const isFilteredMode =
    file.length >= 2 || (isGroupFilterActive && studentsTabFilter.length > 0)
  const currentPage = isFilteredMode ? filterPage : page
  const isEnabled =
    !isProcessingBalance &&
    !isGroupFilterHidingEverything &&
    (isFilteredMode || (inView && isNextPage) || firstRender.current)
  const { data, isLoading, isRefetching, isError, refetch } =
    useQueryClassesStudents({
      classId: Number(classId),
      page: currentPage,
      sorting: sorting,
      sort_by: sortBy,
      limit: 30,
      filter: file,
      group_ids: isGroupFilterActive ? studentsTabFilter : undefined,
      options: {
        cacheTime: 0,
        enabled: isEnabled,
        onSuccess: (data) => {
          if (isEditClass) {
            const result = data?.data.objects.filter((value) => {
              return value.is_math_enabled
            })
            setSelectedItems(result)
          }
          if (isToggleAll) {
            const filterSelected = filteredData(
              data?.data.objects,
              excludedItems,
            )
            if (isFilteredMode) {
              setSelectedItems([...filterSelected])
            } else {
              setSelectedItems((prev) => [...prev, ...filterSelected])
            }
          }
          if (inView && isNextPage) {
            // Clear list on first load (page === 0) to replace instead of append
            if (page === 0) {
              dispatch(setClassList([]))
            }
            if (data?.data.objects.length === 30) {
              dispatch(setIsNextPage(true))
              dispatch(setPage(30))
              if (isRefetchFromUseEffect.current) {
                isRefetchFromUseEffect.current = false
              }
            } else if (data?.data.objects.length < 30) {
              dispatch(setPage(0))
              dispatch(setIsNextPage(false))
            }
          }

          if (isFilteredMode) {
            if (data?.data.objects.length === 30) {
              setFilterPage(30)
            } else if (data?.data.objects.length < 30) {
              setFilterPage(0)
            }
            setFilterElements(data?.data.objects)
            return
          }

          dispatch(setStudentsNumber(data?.data.all_objects_count))

          // Don't execute this block if inView && isNextPage already handled it (prevents double increment)
          if (page === 0 && !(inView && isNextPage)) {
            dispatch(setClassList([]))
            if (data?.data.objects.length === 30) {
              dispatch(setIsNextPage(true))
              // Only update page if this is NOT a refetch from useEffect
              if (!isRefetchFromUseEffect.current) {
                dispatch(setPage(30))
              } else {
                isRefetchFromUseEffect.current = false
              }
            } else {
              dispatch(setIsNextPage(false))
            }
          }
          if (data?.data.objects.length !== 0) {
            dispatch(setClassList(data?.data.objects))
          } else {
            if (page === 0) {
              dispatch(setClassList([]))
            }
          }

          firstRender.current = false
        },
      },
    })

  React.useImperativeHandle(
    ref,
    () => ({
      triggerRefetch() {
        dispatch(setPage(0))
        dispatch(setClassList([]))
        setTimeout(() => {
          refetch()
        }, 0)
      },
    }),
    [],
  )

  useEffect(() => {
    if (!isProcessingBalance) return

    setTimeout(() => {
      const tableContainer = tableContainerRef.current?.querySelector(
        ".smooth-scrollbar",
      ) as HTMLElement
      if (tableContainer) {
        tableContainer.scrollTop = 0
      }
    }, 0)
  }, [isProcessingBalance])

  useEffect(() => {
    if (
      !isProcessingBalance &&
      inView &&
      isNextPage &&
      data?.data.objects.length === 30
    ) {
      isRefetchFromUseEffect.current = true
      refetch()
    }
  }, [inView, isProcessingBalance])

  useEffect(() => {
    // Don't scroll to bottom if we're restoring scroll position from student profile
    const savedScrollPosition = sessionStorage.getItem(
      "classListScrollPosition",
    )
    const isRestoringScroll = savedScrollPosition !== null
    // classname is unchanged when this component remounts (e.g. switching the
    // Students/Groups tab) — only scroll when the class actually changed.
    const didClassnameChange =
      previousClassname.current !== null &&
      previousClassname.current !== classname
    previousClassname.current = classname

    if (
      bottomViewRef.current !== null &&
      !isRestoringScroll &&
      didClassnameChange
    ) {
      bottomViewRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }

    if (!classList.length) {
      firstRender.current = true
      // `cacheTime: 0` on the query below means it's dropped from the QueryClient cache the
      // instant this component unmounts (e.g. switching to the Groups tab to edit
      // membership) — so there's no cache-level "invalidated" mark left to notice on
      // remount. The group mutations reset this Redux `classList` to `[]` directly instead
      // (the one piece of state that does survive the unmount) as their "please refetch"
      // signal. Setting `firstRender.current` alone only takes effect on this component's
      // *next* render, which isn't guaranteed to happen soon — so also fetch explicitly.
      setTimeout(() => refetch(), 0)
    }
  }, [classname])

  useEffect(() => {
    if (!classList.length && page === 0) {
      firstRender.current = true
    }
  }, [classList.length, page])

  useEffect(() => {
    const savedWindowScroll = sessionStorage.getItem("windowScrollPosition")
    const isClassRoute = location.pathname.includes("/class/")

    // Only restore if we're on class route, have saved scroll, and not loading
    if (isClassRoute && savedWindowScroll !== null && !isLoading) {
      const windowScrollPosition = Number(savedWindowScroll)

      // Restore scroll immediately (before render) to avoid showing top first
      window.scrollTo({ top: windowScrollPosition, behavior: "auto" })
      document.documentElement.scrollTop = windowScrollPosition
      document.body.scrollTop = windowScrollPosition

      // Ensure it sticks after DOM updates
      requestAnimationFrame(() => {
        window.scrollTo({ top: windowScrollPosition, behavior: "auto" })
        document.documentElement.scrollTop = windowScrollPosition
        document.body.scrollTop = windowScrollPosition
        sessionStorage.removeItem("classListScrollPosition")
        sessionStorage.removeItem("windowScrollPosition")
      })
    }
  }, [location.pathname, isLoading])

  // Scroll to top when breakpoint changes (desktop/tablet ↔ mobile)
  useEffect(() => {
    const savedPreviousIsTabletValue =
      sessionStorage.getItem("previousIsTablet")
    const previousIsTabletValue =
      savedPreviousIsTabletValue !== null
        ? savedPreviousIsTabletValue === "true"
        : previousIsTablet.current

    if (previousIsTabletValue !== null && previousIsTabletValue !== isTablet) {
      sessionStorage.removeItem("classListScrollPosition")
      sessionStorage.removeItem("windowScrollPosition")

      if (!previousIsTabletValue && isTablet) {
        dispatch(setPage(0))
        dispatch(setClassList([]))
        dispatch(setIsNextPage(true))
        firstRender.current = true
        setTimeout(() => refetch(), 0)
      }

      window.scrollTo({ top: 0, behavior: "auto" })
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0

      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "auto" })
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      })
    }

    previousIsTablet.current = isTablet
    sessionStorage.setItem("previousIsTablet", String(isTablet))
  }, [isTablet, dispatch, refetch])

  useEffect(() => {
    if (location.state !== null) {
      dispatch(setClassList([]))
      dispatch(setPage(0))
      dispatch(setIsNextPage(true))
      setSortBy && setSortBy("first_name")
      setFile("")
    }
  }, [classId])

  useEffect(() => {
    const filterSelected = filteredData(classList, excludedItems)
    if (isFilteredMode) {
      dispatch(setIsNextPage(false))
    } else if (!isFilteredMode && page !== 0) {
      setTimeout(() => {
        dispatch(setIsNextPage(true))
      }, 1000)
      setFilterElements([])
      isToggleAll && setSelectedItems([...filterSelected])
    } else {
      isToggleAll && setSelectedItems([...filterSelected])
    }
  }, [file, isGroupFilterActive, studentsTabFilter?.length]) // eslint-disable-line

  const selectingProps = useSelectedItems(
    isFilteredMode ? filterElements : classList,
  )

  const {
    selectedItems,
    clearSelectedItems,
    setSelectedItems,
    excludedItems,
    clearExcludedItems,
    setExcludedItems,
  } = selectingProps

  useEffect(() => {
    clearSelectedItems()
    clearExcludedItems()
  }, [classId])

  useEffect(() => {
    if (isToggleAll && !excludedItems.length) {
      mixpanel.track("send_transaction", {
        event_name: "select_all",
      })
    } else if (isToggleAll && !!excludedItems.length) {
      mixpanel.track("send_transaction", {
        event_name: "select individuals",
      })
    } else if (
      selectedItems.length !== classList.length &&
      classList.length !== 0 &&
      setIsToggle
    ) {
      setIsToggle(false)
    } else if (
      selectedItems.length === classList.length &&
      classList.length !== 0 &&
      setIsToggle
    ) {
      setIsToggle(true)
    }
  }, [isToggleAll, excludedItems, selectedItems])

  const [isExportData, setIsExportData] = useState(false)

  const { isFetching, refetch: refetchExportData } =
    useQueryExportClassStudents({
      classId: Number(classId),
      options: {
        enabled: isExportData,
        onSuccess: (response) => {
          setIsExportData(false)
          handleExportSuccess(response, Number(classId))
          showSnackbar({
            title: t("export.fileIsCurrentlyBeingGenerated"),
          })
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const { isGenerateDisabled } = useExportGuard({
    hasPendingTasks: pendingTasks.length > 0,
  })

  return (
    <>
      <PrimaryTile
        sx={{
          borderRadius: "0px 8px 8px 8px",
          overflow: isProcessingBalance ? "hidden" : undefined,
          position: isProcessingBalance ? "relative" : undefined,
        }}
      >
        <Box
          sx={{
            display: "flex",
            columnGap: "10px",
            alignItems: "center",
            mb: 2,
          }}
        >
          <SearchbarFormWithButtons
            searchbarFormProps={{
              placeholder: `${t("students.searchForStudent")}...`,
              onSubmit: (data) => setFile(data.search),
            }}
            buttonTitle={t("students.addStudent")}
            onButtonClick={(e) => {
              e && setPopoverAnchorEl(e?.currentTarget)
              mixpanel.track("add_students", {
                event_name: "add_student_button",
                action: "open",
              })
            }}
            actionSlot={<ListingDisplayModeButtons />}
            endSlot={
              <Stack direction="row" alignItems="center">
                <Tooltip title={t("group.moreOptions")} arrow>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={t("group.moreOptions")}
                    aria-haspopup="true"
                    aria-expanded={Boolean(overflowMenuAnchor)}
                    onClick={(e) => {
                      setOverflowMenuAnchor(e.currentTarget)
                      refreshTasks()
                    }}
                    sx={(muiTheme) => ({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      transition: "background-color 0.15s",
                      border: `1px solid ${muiTheme.palette.primary.main}`,
                      ...(isGroupFilterActive
                        ? {
                            color: muiTheme.palette.primary.main,
                            bgcolor: muiTheme.palette.mockup.primary95,
                            "&:hover": {
                              bgcolor: muiTheme.palette.mockup.primary90,
                            },
                          }
                        : {
                            color: muiTheme.palette.mockup.neutral40,
                            "&:hover": {
                              bgcolor: muiTheme.palette.mockup.neutral95,
                            },
                          }),
                    })}
                  >
                    <IconThreeDots />
                  </Box>
                </Tooltip>
                <PopOverStyle
                  anchorEl={overflowMenuAnchor}
                  open={Boolean(overflowMenuAnchor)}
                  onClose={() => {
                    setOverflowMenuAnchor(null)
                    setIsExportRowOpen(false)
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <ul style={{ width: "250px" }}>
                    <li>
                      <PopRow
                        sx={{ width: "100%" }}
                        onClick={() => setIsExportRowOpen((prev) => !prev)}
                      >
                        <PopoverBox>
                          <Typography component="span" variant="body2" fontWeight="500">
                            {t("group.export")}
                          </Typography>
                          <IconChevronDown
                            style={{
                              width: "16px",
                              height: "16px",
                              marginLeft: "auto",
                              transform: isExportRowOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </PopoverBox>
                      </PopRow>
                    </li>
                    <InlineExportSubmenu
                      open={isExportRowOpen}
                      successTasks={successTasks}
                      onGenerateNewFile={() =>
                        schoolPlanToExportData({
                          user: userInfo,
                          upgrade: handleOpenExport,
                          exportData: refetchExportData,
                        })
                      }
                      onFileClick={setExportReportIdToDownload}
                      isGenerateDisabled={isGenerateDisabled || isFetching}
                      generateDisabledTooltip={t(
                        "export.reportCurrentlyBeingGenerated",
                      )}
                    />
                    <Tooltip
                      title={
                        isGroupFilterDisabled
                          ? t("group.groupFilterNoGroupsTooltip")
                          : ""
                      }
                      arrow
                      placement="left"
                    >
                      <li>
                        <PopRow
                          sx={{
                            width: "100%",
                            ...(isGroupFilterDisabled && {
                              cursor: "default",
                              opacity: 0.5,
                              "&:hover": { backgroundColor: "transparent" },
                            }),
                          }}
                          disabled={isGroupFilterDisabled}
                          onClick={() => {
                            const anchor = overflowMenuAnchor
                            setOverflowMenuAnchor(null)
                            setIsExportRowOpen(false)
                            if (isFreeDemo) {
                              handleOpenGroupsUpgrade()
                              return
                            }
                            if (anchor) setGroupFilterAnchor(anchor)
                          }}
                        >
                          <PopoverBox justifyContent="space-between">
                            {t("group.groupFilterMenuItem")}
                            {isGroupFilterActive && (
                              <Box
                                aria-hidden="true"
                                sx={{
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  bgcolor: "primary.main",
                                }}
                              />
                            )}
                          </PopoverBox>
                        </PopRow>
                      </li>
                    </Tooltip>
                  </ul>
                </PopOverStyle>
              </Stack>
            }
            isAddNewClass={isEditClass}
            savingsAccountOption={savingsAccountOption}
          />
          {hasGroups && (
            <GroupFilterMenu
              anchorEl={groupFilterAnchor}
              open={Boolean(groupFilterAnchor)}
              onClose={() => setGroupFilterAnchor(null)}
              groups={allGroups}
              savedSelection={studentsTabFilter}
              defaultAllChecked={false}
              title={t("group.groupFilters")}
              helperText={t("group.selectGroupsToDisplayStudents")}
              onApply={(ids) => {
                saveGroupFilter({
                  classId: Number(classId),
                  data: { students_tab: ids.length === 0 ? null : ids },
                })
                setGroupFilterAnchor(null)
              }}
              onReset={() => {
                saveGroupFilter({
                  classId: Number(classId),
                  data: { students_tab: null },
                })
                setGroupFilterAnchor(null)
              }}
            />
          )}
          <DialogUpgradeAccount
            open={isOpenExport}
            onClose={() => {
              handleCloseExport()
              mixpanel.track("reports_export", {
                method: "class_students",
                action: "cancel",
              })
            }}
            onActionButtonClick={() => {
              payIt(null)
              mixpanel.track("reports_export", {
                method: "class_students",
                action: "submit",
              })
            }}
            isLoading={isLoadingPayments}
            isExport
          />
          <DialogUpgradeAccount
            open={isOpenGroupsUpgrade}
            onClose={handleCloseGroupsUpgrade}
            onActionButtonClick={() => {
              payIt(null)
              handleCloseGroupsUpgrade()
            }}
            isLoading={isLoadingPayments}
            isGroups
          />
        </Box>
        {taskData?.status === "PENDING" && (
          <Box role="status" aria-live="polite">
            <LinearProgress
              color="primary"
              aria-label={t("defaultTranslations.loading")}
            />
          </Box>
        )}
        {taskData?.status === "IN_PROGRESS" && (
          <ProgressBar value={taskData.progress} />
        )}
      </PrimaryTile>
      <Box
        sx={{
          position: "sticky",
          top: { mobile: "96px", tablet: "64px", desktop: "68px" },
          zIndex: 1100,
          marginBottom: "0px",
          display: selectedItems.length > 0 ? "block" : "none",
        }}
      >
        <BatchActionButtons
          isLoading={isLoading}
          studentsNumber={studentsNumber}
          file={file}
          isGroupFilterActive={
            isGroupFilterActive && !!studentsTabFilter?.length
          }
          stateSelectedItems={{ setSelectedItems, selectedItems }}
          stateExcludedItems={{ setExcludedItems, excludedItems }}
          stateIsToggleAll={{ setIsToggle, isToggleAll }}
          clearSelectedItems={clearSelectedItems}
          clearExcludedItems={clearExcludedItems}
        />
      </Box>
      <PrimaryTile
        sx={{
          borderRadius: "8px",
          overflow: isProcessingBalance ? "hidden" : undefined,
          position: isProcessingBalance ? "relative" : undefined,
        }}
      >
        <Box
          ref={tableContainerRef}
          sx={{
            position: "relative",
            minHeight: isProcessingBalance ? "400px" : undefined,
            width: "100%",
            maxHeight: isProcessingBalance ? "calc(100vh - 350px)" : undefined,
            height: isProcessingBalance ? "calc(100vh - 350px)" : undefined,
            overflow: isProcessingBalance ? "hidden" : undefined,
            "& .smooth-scrollbar": {
              overflow: isProcessingBalance ? "hidden !important" : undefined,
            },
          }}
        >
          {!isProcessingBalance &&
            renderDataFromBE &&
            renderDataFromBE({
              apiData: isGroupFilterHidingEverything
                ? []
                : isFilteredMode
                  ? filterElements
                  : classList,
              selectingProps,
              invokeListItemAction,
            })}
          {isProcessingBalance && (
            <ProcessingOverlay
              status={resetBalanceStatus}
              onComplete={() => handleResetBalanceOutcome("SUCCESS")}
              onError={() => handleResetBalanceOutcome("FAILURE")}
            />
          )}
          {isLoading && page === 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "500px",
                width: "100%",
              }}
            >
              <LoadingIndicator />
            </Box>
          )}
          {isError && <ErrorText />}
          {isGroupFilterHidingEverything && (
            <NoElementsMessage
              boxStyle={{ py: { mobile: "16px", tablet: "62px" } }}
              Icon={<SvgPhoto />}
              title={t("group.allGroupsHiddenTitle")}
              description={t("group.allGroupsHiddenDescription")}
            />
          )}
          {!isGroupFilterHidingEverything &&
            !isLoading &&
            !!file.length &&
            !filterElements.length && <NoMatchesForSearch search={file} />}
          {!isGroupFilterHidingEverything &&
            !isLoading &&
            isGroupFilterActive &&
            !file.length &&
            !filterElements.length && (
              <NoElementsMessage
                boxStyle={{ py: { mobile: "16px", tablet: "62px" } }}
                Icon={<SvgPhoto />}
                title={t("group.noStudentsMatchGroupFilter")}
              />
            )}
          {!isGroupFilterHidingEverything &&
            !isLoading &&
            !isRefetching &&
            !file.length &&
            !isGroupFilterActive &&
            classList.length === 0 &&
            (studentsNumber === 0 || !isNextPage) && (
              <NoElementsMessage
                boxStyle={{ py: { mobile: "16px", tablet: "62px" } }}
                Icon={<SvgPhoto />}
                title={t("students.youDoNotHaveAnyStudentsYet")}
                description={t("students.addStudentsManuallyOrHaveStudents")}
              />
            )}
        </Box>
        <PopoverSearchbarButton
          sx={{
            "& .MuiTypography-root": {
              ":last-child": {
                opacity: taskData ? 0.3 : 1,
              },
            },
          }}
          open={!!popoverAnchorEl}
          anchorEl={popoverAnchorEl}
          onClose={handleClose}
          items={[
            {
              name: t("students.studentsInfo"),
              onClick: () => {
                handleOpenInfo()
                mixpanel.track("add_students", {
                  event_name: "student_info",
                  action: "open",
                })
              },
            },
            {
              name: t("students.importStudents"),
              onClick: () => {
                if (taskData) return
                handleOpenImport()
                mixpanel.track("add_students", {
                  event_name: "import_students",
                  action: "open",
                })
              },
            },
            {
              name: t("students.addAllStudents"),
              onClick: () => {
                if (userInfo !== null && isSchoolPlan(userInfo)) {
                  handleOpenAllStudents()
                } else {
                  handleOpenDialogUpgradeAccount()
                }
              },
            },
          ]}
        />
        <DialogAddAllStudents
          open={isOpenAllStudents}
          onClose={handleCloseAllStudents}
        />
        <DialogUpgradeAccount
          customActionButtonsSlot={<></>}
          open={isDialogOpenUpgradeAccount}
          onClose={handleCloseDialogUpgradeAccount}
          isLoading={false}
          onActionButtonClick={() => {}}
          isSchool
        />
        <DialogFormStudentsInfo
          isOpen={isOpenInfo}
          handleClose={() => {
            handleCloseInfo()
            handleClose()
          }}
          refetch={refetch}
        />

        <DialogImport
          isOpen={isOpenImport}
          onClose={() => {
            handleCloseImport()
            mixpanel.track("add_students", {
              method: "import_students",
              action: "cancel",
            })
          }}
          classId={classId}
          refetch={refetch}
          clearSelectedItems={clearSelectedItems}
          clearExcludedItems={clearExcludedItems}
          setIsToggleAll={setIsToggle}
        />

        {!isProcessingBalance &&
          renderDataFromBEOutside &&
          renderDataFromBEOutside({
            apiData: isGroupFilterHidingEverything
              ? []
              : isFilteredMode
                ? filterElements
                : classList,
            selectingProps,
            invokeListItemAction,
          })}
      </PrimaryTile>
      {!isProcessingBalance && <Box ref={inViewRef}></Box>}
      <Box ref={bottomViewRef} />
      {displayMode === "grid" && isRefetching && (
        <Box
          sx={{
            paddingY: 2,
            width: "100%",
            textAlign: "center",
          }}
        >
          <LoadingIndicator />
        </Box>
      )}
      {((displayMode === "list" && isRefetching) ||
        (page !== 0 && isLoading)) && (
        <Box
          sx={{
            paddingY: 2,
            width: "100%",
            textAlign: "center",
          }}
        >
          <LoadingIndicator />
        </Box>
      )}
      <ListItemActionsHandler
        action={action}
        resetActionState={resetActionState}
      />
      <ExportPendingBanner visible={pendingTasks.length > 0} />
    </>
  )
}

export default React.forwardRef(ViewCommonElementsContainer)
